import { conversationMatchesAssigneeId } from './conversationListFilters';

const DEFAULT_RIGHT_SIDEBAR_WIDTH = 400;
const MIN_CARD_WIDTH_WITH_SIDEBAR = 320;
export const CONVERSATION_CARD_EXPANDED_MIN_WIDTH = 720;
const SCHEDULED_EMAIL_TOOLTIP_KEY =
  'CONVERSATION.CARD_TOOLTIP.HAS_SCHEDULED_EMAIL';
const SCHEDULED_MESSAGE_TOOLTIP_KEY =
  'CONVERSATION.CARD_TOOLTIP.HAS_SCHEDULED_MESSAGE';
export const CONVERSATION_LIST_SECTION_ROW_KEY = '_isSection';
const STAR_OFF_TOOLTIP_KEY = 'CONVERSATION.CARD_TOOLTIP.SPECIAL_OFF';
const STAR_ON_TOOLTIP_KEY = 'CONVERSATION.CARD_TOOLTIP.SPECIAL_ON';

export function resolveConversationCardEffectiveWidth({
  availableWidth = null,
  minWidthWithSidebar = MIN_CARD_WIDTH_WITH_SIDEBAR,
  rightSidebarWidth = DEFAULT_RIGHT_SIDEBAR_WIDTH,
  showRightSidebar = false,
  windowWidth = 0,
} = {}) {
  if (availableWidth) {
    return availableWidth;
  }

  if (!showRightSidebar) {
    return windowWidth;
  }

  return Math.max(minWidthWithSidebar, windowWidth - rightSidebarWidth);
}

export function shouldUseConversationCardExpandedLayout({
  effectiveWidth = 0,
  minExpandedWidth = CONVERSATION_CARD_EXPANDED_MIN_WIDTH,
  prefersExpanded = false,
} = {}) {
  return Boolean(prefersExpanded && effectiveWidth >= minExpandedWidth);
}

export function shouldShowConversationMessagesCount({
  count = null,
  isTelegramChannel = false,
  isWhatsappChannel = false,
} = {}) {
  if (typeof count !== 'number' || count <= 1) {
    return false;
  }

  return !isWhatsappChannel && !isTelegramChannel;
}

export function resolveConversationScheduledIndicatorTooltip({
  hasScheduledMessage = false,
  translate = null,
} = {}) {
  if (!translate) {
    return '';
  }

  const key = hasScheduledMessage
    ? SCHEDULED_MESSAGE_TOOLTIP_KEY
    : SCHEDULED_EMAIL_TOOLTIP_KEY;
  const translated = translate(key);

  return translated === key
    ? translate(SCHEDULED_EMAIL_TOOLTIP_KEY)
    : translated;
}

export function resolveConversationStarTooltip({
  isStarred = false,
  translate = null,
} = {}) {
  if (!translate) {
    return '';
  }

  return translate(isStarred ? STAR_ON_TOOLTIP_KEY : STAR_OFF_TOOLTIP_KEY);
}

export function buildChatListToolbarProps({
  activeFilterAgentId = null,
  activeStatus = 'open',
  activeTab = null,
  currentPage = 1,
  hasAppliedFilters = false,
  hasPriorityConversations = false,
  isLoading = false,
  isSearchResults = false,
  items = [],
  itemsPerPage = 30,
  pageTitle = '',
  priorityFilterEnabled = false,
  selectedInboxId = null,
  totalItems = 0,
  unreadCount = 0,
  unreadFirstEnabled = false,
} = {}) {
  return {
    items,
    activeTab,
    unreadCount,
    selectedInboxId,
    totalItems,
    currentPage,
    itemsPerPage,
    isLoading,
    pageTitle,
    hasAppliedFilters,
    activeStatus,
    activeFilterAgentId,
    unreadFirstEnabled,
    priorityFilterEnabled,
    hasPriorityConversations,
    isSearchResults,
  };
}

export function shouldShowConversationListEndMessage({
  chatListLoading = false,
  currentPage = 1,
  totalPages = 1,
  visibleSelectableConversationsLength = 0,
} = {}) {
  return Boolean(
    visibleSelectableConversationsLength &&
    currentPage === totalPages &&
    !chatListLoading
  );
}

export function shouldShowConversationListLoadingIndicator({
  chatListLoading = false,
  isFetchingConversations = false,
  isSearchModeActive = false,
  isSilentRefresh = false,
  paginationLoading = false,
  searchResultsLoading = false,
} = {}) {
  if (isSearchModeActive) {
    return searchResultsLoading;
  }

  return (
    (paginationLoading || isFetchingConversations || chatListLoading) &&
    !isSilentRefresh
  );
}

export function shouldShowConversationListEmptyState({
  chatListLoading = false,
  isSearchModeActive = false,
  searchResultsError = false,
  searchResultsLength = 0,
  searchResultsLoading = false,
  visibleSelectableConversationsLength = 0,
} = {}) {
  if (isSearchModeActive) {
    return (
      !searchResultsLoading && !searchResultsError && searchResultsLength === 0
    );
  }

  return !chatListLoading && visibleSelectableConversationsLength === 0;
}

export function filterVisibleConversations({
  conversationList = [],
  filterContactId = null,
  includeLabeledConversations = false,
  isSearchModeActive = false,
  searchQuery = '',
  searchResults = [],
  selectedAssigneeIdFilter = null,
} = {}) {
  if (isSearchModeActive) {
    return searchResults;
  }

  let list = conversationList;

  if (filterContactId !== null) {
    return list.filter(
      (conversation) => conversation.meta?.sender?.id === filterContactId
    );
  }

  if (selectedAssigneeIdFilter !== null) {
    list = list.filter((conversation) =>
      conversationMatchesAssigneeId(conversation, selectedAssigneeIdFilter)
    );
  }

  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    list = list.filter((conversation) => {
      const contactName = conversation.meta?.sender?.name?.toLowerCase() || '';
      const contactEmail =
        conversation.meta?.sender?.email?.toLowerCase() || '';
      const contactPhone =
        conversation.meta?.sender?.phone_number?.toLowerCase() || '';

      return (
        contactName.includes(query) ||
        contactEmail.includes(query) ||
        contactPhone.includes(query)
      );
    });
  }

  if (includeLabeledConversations) {
    list = list.filter(
      (conversation) => conversation.labels && conversation.labels.length > 0
    );
  }

  return list;
}

export function buildConversationListSections({
  filteredConversationList = [],
  priorityFilterEnabled = false,
  unreadFirstEnabled = false,
  readSectionLabel = '',
  unreadSectionLabel = '',
} = {}) {
  if (priorityFilterEnabled) {
    return filteredConversationList.filter(
      (conversation) => conversation.priority != null
    );
  }

  if (!unreadFirstEnabled) return filteredConversationList;

  const unread = filteredConversationList.filter(
    (conversation) => !conversation.agent_last_seen_at
  );
  const read = filteredConversationList.filter(
    (conversation) => conversation.agent_last_seen_at
  );

  const result = [];
  if (unread.length) {
    result.push({
      id: '__section_unread',
      [CONVERSATION_LIST_SECTION_ROW_KEY]: true,
      label: unreadSectionLabel,
      count: unread.length,
    });
    result.push(...unread);
  }
  if (read.length) {
    result.push({
      id: '__section_read',
      [CONVERSATION_LIST_SECTION_ROW_KEY]: true,
      label: readSectionLabel,
      count: read.length,
    });
    result.push(...read);
  }

  return result;
}
